import { StrictMode } from 'react';
import './i18n/index.ts';
import { createRoot } from 'react-dom/client';
import { setActiveApp, getRuntimeConfig } from '@quickbee/api-config';
import { initAuthSession } from '@quickbee/auth/internal';
import { initTenant, sessionOptions } from '@/lib/tenant';
import { assertThemeWiring } from '@/theme';
import './index.css';
import App from './App.tsx';

// Bootstrap order matters (design doc §4.3): the app name feeds the
// single-active-session slot on every request, so it must be set before the
// first client is constructed. The backend's slot for this persona is
// `customer`, not `pwa`.
setActiveApp('customer');

// Reading the config here surfaces a malformed injection at boot rather than
// as a silent fall-through into offline/mock mode mid-session.
getRuntimeConfig();

void (async () => {
  // Host-resolved tenant first: the refresh call (and every auth mutation)
  // must carry X-Tenant-ID or TenantResolutionFilter refuses it.
  await initTenant();

  // Silent bootstrap: sends the HttpOnly refresh cookie; on success hydrates the
  // store and schedules the proactive refresh cycle. The splash screen is the
  // visual cover for this. App-lifetime session — the disposer is unused.
  //
  // This used to be skipped entirely when `isOffline()` (an empty apiOrigin, i.e.
  // a fixtures build), on the reasoning that there was no backend to refresh
  // against. `sessionClient` now serves `/auth/*` from fixtures in mock mode, so
  // that shortcut actively broke the demo build: it cleared the store on every
  // load, and a signed-in user was thrown back to /auth/login by the next reload
  // or deep link. Refreshing is safe in every mode — mock mode resolves it
  // locally, and `initAuthSession` settles the store to unauthenticated by itself
  // when nothing is restored, which is all `clearAuth()` was achieving here.
  void initAuthSession(sessionOptions());
})();

// Dev-only: catches a theme colour that exists as a variable but not as a
// generated utility class — the silent "half the chrome vanished" failure.
assertThemeWiring();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
